import React, {
  useState,
  useContext,
  FC,
  useEffect,
  useMemo,
  useCallback,
  createContext,
  useRef,
} from 'react'
import { DateTime } from 'luxon'
import { getUserInfo } from '../apiClient/skeema/fetchWrappers'
import { UserInfoType } from '../models/user.types'
import { useNavigate } from 'react-router-dom'
import { WEBAPP_SESSION_STORAGE_KEYS } from '../models/browserStorage.types'
import { useExtensionHealthContext } from './ExtensionHealthContext'
import { BACKGROUND_ON_MESSAGE_LISTENER_ACTIONS } from '../extension/models/messaging.types'
import { sendMessageToExtension } from '../webapp/utils/externalMessaging'
import { CURRENT_ENV, MINIMUM_EXTENSION_VERSION, WEBAPP_BASE_URL } from '../constants'
import * as Sentry from '@sentry/react'
import { useAuthContext } from './AuthContext'

const EXTENSION_NOT_REQUIRED_URLS: string[] = [
  `${WEBAPP_BASE_URL}/extension/uninstalled`,
  `${WEBAPP_BASE_URL}/projects`,
  `${WEBAPP_BASE_URL}/folders`,
  `${WEBAPP_BASE_URL}/mobile/signup`,
]

type UserContextType = {
  userInfo: UserInfoType | undefined
  isUserInfoLoading: boolean
  isExtensionRequired: boolean | undefined
  captureAnalytics: (
    event: string,
    properties?: Record<string, unknown>,
    options?: { sendInstantly?: boolean },
  ) => void
  signOut: () => Promise<void>
}

export const UserContext = createContext<UserContextType>({
  userInfo: undefined,
  isUserInfoLoading: true,
  isExtensionRequired: undefined,
  captureAnalytics: () => {},
  signOut: () => {
    console.error('ERROR: UserContext signOut function called before being initialized')
    return Promise.resolve()
  },
})

export const useUserContext = (): UserContextType => {
  return useContext(UserContext)
}

export const UserContextProvider: FC<{
  isInExtension: boolean
  trackFacebookEvent?: (event: string, properties?: Record<string, unknown>) => void
  posthog:
    | {
        identify: (email: string, userInfo: object) => void
        capture: (
          event: string,
          properties?: Record<string, unknown>,
          options?: { send_instantly?: boolean },
        ) => void
        reset: () => void
      }
    | undefined
  children: React.ReactNode
}> = ({ isInExtension, trackFacebookEvent, posthog, children }): React.JSX.Element => {
  const [isUserInfoLoading, setIsUserInfoLoading] = useState<boolean>(true)
  const [userInfo, setUserInfo] = useState<UserInfoType | undefined>(undefined)
  const navigate = useNavigate()
  const isExtensionRequired = isInExtension
    ? undefined
    : !EXTENSION_NOT_REQUIRED_URLS.some((url) => window.location.href.startsWith(url))

  const { extensionHealth, forceReload: forceExtensionHealthReload } = useExtensionHealthContext()

  const { authHeader, isAuthLoading, isAuthenticated, signOut: _signOut } = useAuthContext()

  const signOut = useCallback(async () => {
    await _signOut()
    setUserInfo(undefined)
    //TODO: message extension to clear auth
    window.sessionStorage.removeItem(WEBAPP_SESSION_STORAGE_KEYS.SkeemaUserBrowserSession)
    posthog?.reset?.()
    navigate('/extension/installed')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, posthog])

  const captureAnalytics = useCallback(
    (
      event: string,
      properties?: Record<string, unknown>,
      options?: { sendInstantly?: boolean },
    ) => {
      const timeSinceSignUp = userInfo?.date_joined
        ? Math.floor(DateTime.now().toMillis() - DateTime.fromISO(userInfo.date_joined).toMillis())
        : undefined
      const updatedProperties = {
        ...properties,
        timeSinceSignUp,
      }
      if (CURRENT_ENV !== 'prod') {
        console.log('Capturing analytics event', event, updatedProperties)
      }
      posthog?.capture?.(
        event,
        updatedProperties,
        options?.sendInstantly ? { send_instantly: true } : undefined,
      )
      if (trackFacebookEvent) {
        trackFacebookEvent(event, updatedProperties)
      }
    },
    [posthog, userInfo, trackFacebookEvent],
  )

  const initSession = useCallback(
    (userInfo: UserInfoType) => {
      const sessionId = window.sessionStorage.getItem(
        WEBAPP_SESSION_STORAGE_KEYS.SkeemaUserBrowserSession,
      )
      console.log('User', userInfo, 'Session ID', sessionId)
      if (sessionId === null) {
        posthog?.identify?.(userInfo.email, userInfo)
        Sentry.setUser({ email: userInfo.email })
        const sessionId = Math.random().toString(36).substring(2, 15)
        window.sessionStorage.setItem(
          WEBAPP_SESSION_STORAGE_KEYS.SkeemaUserBrowserSession,
          sessionId,
        )
        console.log('INIT SESSION: ', sessionId)
      }
    },
    [posthog],
  )

  useEffect(() => {
    if (isAuthLoading) {
      return
    }

    if (!isAuthenticated || !authHeader) {
      setUserInfo(undefined)
      setIsUserInfoLoading(false)
      return
    }

    if (userInfo) {
      return
    }

    if (isExtensionRequired && extensionHealth === undefined) {
      return
    }

    const timezone = DateTime.now().zoneName
    const headers = { Authorization: authHeader }
    setIsUserInfoLoading(true)
    void getUserInfo(headers, extensionHealth ? extensionHealth.version : undefined, timezone)
      .then((userInfo) => {
        console.log('User info USERCONTEXT', userInfo)
        setUserInfo(userInfo.data)
        initSession(userInfo.data)
      })
      .catch((err) => {
        console.error(err)
        // we used to sign user out here, which is not the best idea
        // sometimes it's okay to not be able to fetch user info if it's already known locally
        // even when it's fatal we should show an appropriate error message instead
      })
      .finally(() => {
        setIsUserInfoLoading(false)
      })
  }, [
    authHeader,
    extensionHealth,
    initSession,
    isAuthLoading,
    isAuthenticated,
    isExtensionRequired,
    signOut,
    userInfo,
  ])

  const hasIdentified = useRef(false)

  useEffect(() => {
    console.log('EXTENSION HEALTH: ', userInfo, extensionHealth)
    if (isInExtension) {
      return
    }

    if (!isExtensionRequired) {
      console.log('Extension not required')
      return
    }

    if (extensionHealth === undefined) {
      // Extension health is not initialized
      return
    }
    if (extensionHealth === null) {
      // Extension health failed to initialize
      navigate('/extension/install')
      return
    }
    if (extensionHealth.version < MINIMUM_EXTENSION_VERSION) {
      console.error(
        `Extension version ${extensionHealth.version} is less than minimum required version ${MINIMUM_EXTENSION_VERSION}`,
      )
      navigate('/extension/update')
      return
    }

    if (!userInfo) {
      return
    }

    if (
      authHeader !== undefined &&
      (extensionHealth.userInfo === undefined || extensionHealth.userInfo.email !== userInfo.email)
    ) {
      // Extension is either not logged in or logged in as a different user
      // Update extension auth with webapp auth
      const token = authHeader.replace('Bearer', '').trim()
      sendMessageToExtension(BACKGROUND_ON_MESSAGE_LISTENER_ACTIONS.UPDATE_AUTH, {
        token,
      })
        .then(() => {
          console.log('Updated extension auth with webapp token')

          forceExtensionHealthReload()
        })
        .catch((e) => {
          // Message failed (e.g. service worker mid-restart) — do not sign out,
          // that would clear auth for a transient connectivity error
          console.error('ERROR: Failed to update faulty extension auth', e)
        })

      return
    }

    if (!hasIdentified.current) {
      posthog?.identify?.(userInfo.email, {
        extensionVersion: extensionHealth.version,
      })
      hasIdentified.current = true
    }
  }, [
    authHeader,
    extensionHealth,
    forceExtensionHealthReload,
    isExtensionRequired,
    navigate,
    signOut,
    userInfo,
    posthog,
    isInExtension,
  ])

  const value = useMemo(
    () => ({
      userInfo,
      isUserInfoLoading,
      signOut,
      isExtensionRequired,
      captureAnalytics,
    }),
    [userInfo, isUserInfoLoading, signOut, isExtensionRequired, captureAnalytics],
  )

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}
